import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { 
  BookOpen, ArrowLeft, MessageCircle, Send, Loader2, 
  User, Users, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GroupChatPage = ({ user }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [philosophers, setPhilosophers] = useState([]);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchHistory();
    fetchParticipants();
    fetchPhilosophers();
    
    // Poll for new messages every 3 seconds
    pollRef.current = setInterval(() => {
      fetchHistory(false);
    }, 3000);
    
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const fetchPhilosophers = async () => {
    try {
      const response = await axios.get(`${API}/philosophers`);
      setPhilosophers(response.data);
    } catch (error) {
      console.error("Error fetching philosophers:", error);
    }
  };

  const getPhilosopherAvatar = (name) => {
    const phil = philosophers.find(p => p.name === name);
    return phil?.avatar || null;
  };

  const getPhilosopherColor = (name) => {
    const colors = {
      "Platon": "#8B4513",
      "Aristote": "#2F4F4F",
      "René Descartes": "#4169E1",
      "Emmanuel Kant": "#800020",
      "Friedrich Nietzsche": "#8B0000",
      "Jean-Paul Sartre": "#2F2F2F",
      "Baruch Spinoza": "#006400",
      "Jean-Jacques Rousseau": "#228B22",
      "Karl Marx": "#8B0000",
      "Sigmund Freud": "#4A4A4A",
      "Hannah Arendt": "#6B4423",
      "Georg Wilhelm Friedrich Hegel": "#483D8B"
    };
    return colors[name] || "#1A365D";
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchHistory = async (showLoading = true) => {
    try {
      if (showLoading) setHistoryLoading(true);
      const response = await axios.get(`${API}/chat/group/${encodeURIComponent(user.class_name)}`);
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      if (showLoading) setHistoryLoading(false);
    }
  };

  const fetchParticipants = async () => {
    try {
      const response = await axios.get(`${API}/chat/group/${encodeURIComponent(user.class_name)}/participants`);
      setParticipants(response.data);
    } catch (error) {
      console.error("Error fetching participants:", error);
    }
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat/group`, {
        student_id: user.id,
        class_name: user.class_name,
        message: userMessage
      });
      
      if (response.data.blocked) {
        toast.error(response.data.reason);
        return;
      }
      
      // Add messages to state
      setMessages(prev => [
        ...prev,
        response.data.student_message,
        ...response.data.philosopher_responses
      ]);
      
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Erreur d'envoi. Veuillez réessayer.");
      setInput(userMessage);
    } finally {
      setLoading(false);
    }
  };

  const getPhilosopherInitials = (name) => {
    return name.split(" ").map(n => n[0]).join("").slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col" data-testid="group-chat-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-[#1A365D]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="navbar-logo hidden sm:block">Agora Philosophique</span>
            <span className="text-xs text-[#64748B] hidden sm:block">{user.class_name}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge className="bg-[#22C55E]">{participants.length + 12} en ligne</Badge>
        </div>
      </nav>

      <div className="flex-1 flex max-w-6xl mx-auto w-full px-4">
        {/* Main Chat */}
        <div className="flex-1 flex flex-col">
          {/* Philosophers Banner */}
          <div className="bg-[#1A365D] text-white p-3 rounded-t-lg">
            <p className="text-sm text-center">
              <Sparkles className="w-4 h-4 inline mr-2" />
              Les philosophes sont présents et prêts à débattre avec vous !
            </p>
          </div>

          {/* Messages */}
          <ScrollArea 
            ref={scrollRef}
            className="flex-1 bg-white border-x border-[#E2E8F0] py-4"
            data-testid="messages-container"
          >
            {historyLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 text-[#1A365D] animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mb-4">
                  <Users className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <h2 className="text-xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Bienvenue à l'Agora !
                </h2>
                <p className="text-[#64748B] mb-4 max-w-md">
                  Posez vos questions, débattez avec vos camarades. Les philosophes interviendront pour enrichir la discussion.
                </p>
              </div>
            ) : (
              <div className="space-y-4 px-4">
                {messages.map((message, idx) => {
                  const isStudent = message.role === "student";
                  const isCurrentUser = message.student_id === user.id;
                  const philosopherAvatar = !isStudent ? getPhilosopherAvatar(message.role) : null;
                  
                  return (
                    <div 
                      key={idx}
                      className={`flex gap-3 ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${idx}`}
                    >
                      {!isCurrentUser && (
                        <Avatar className="w-10 h-10 flex-shrink-0 border-2" style={{
                          borderColor: isStudent ? '#D4AF37' : getPhilosopherColor(message.role),
                          backgroundColor: isStudent ? '#D4AF37' : getPhilosopherColor(message.role)
                        }}>
                          {philosopherAvatar ? (
                            <AvatarImage src={philosopherAvatar} alt={message.role} className="object-cover" />
                          ) : null}
                          <AvatarFallback className="text-white text-sm">
                            {isStudent ? message.student_name?.split(" ").map(n => n[0]).join("") : message.role?.split(" ").map(n => n[0]).slice(0,2).join("")}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      
                      <div className={`max-w-[70%] ${isCurrentUser ? 'text-right' : ''}`}>
                        <div className="flex items-center gap-2 mb-1">
                          {!isCurrentUser && (
                            <span className={`text-sm font-medium ${isStudent ? 'text-[#1A365D]' : 'text-[#D4AF37]'}`}>
                              {isStudent ? message.student_name : message.role}
                            </span>
                          )}
                          {!isStudent && (
                            <Badge variant="outline" className="text-xs border-[#D4AF37] text-[#D4AF37]">
                              Philosophe
                            </Badge>
                          )}
                        </div>
                        
                        <div 
                          className={`rounded-lg px-4 py-3 ${
                            isCurrentUser 
                              ? 'bg-[#1A365D] text-white rounded-tr-none' 
                              : isStudent 
                                ? 'bg-[#F8F9FA] border border-[#E2E8F0] rounded-tl-none'
                                : 'bg-gradient-to-r from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/30 rounded-tl-none'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </div>
                        
                        <span className="text-xs text-[#64748B] mt-1 block">
                          {new Date(message.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      
                      {isCurrentUser && (
                        <Avatar className="w-10 h-10 flex-shrink-0 bg-[#1A365D]">
                          <AvatarFallback className="bg-[#1A365D] text-white">
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                      )}
                    </div>
                  );
                })}
                {loading && (
                  <div className="flex gap-3 justify-start">
                    <Avatar className="w-10 h-10 bg-[#D4AF37]">
                      <AvatarFallback className="bg-[#D4AF37]">
                        <Sparkles className="w-5 h-5 text-[#0F172A]" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-4 py-3 rounded-lg rounded-tl-none">
                      <Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="bg-white border border-[#E2E8F0] rounded-b-lg p-4">
            <form onSubmit={sendMessage} className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Participez au débat philosophique..."
                className="flex-1 border-[#E2E8F0] focus:border-[#1A365D]"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button 
                type="submit" 
                disabled={!input.trim() || loading}
                className="bg-[#D4AF37] hover:bg-[#B3932E] text-[#0F172A]"
                data-testid="send-button"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* Sidebar - Participants */}
        <div className="hidden lg:block w-64 ml-4">
          <Card className="card-sjh sticky top-20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#1A365D]">Participants</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="text-xs text-[#64748B] uppercase font-medium">Philosophes</div>
                {["Platon", "Descartes", "Kant", "Sartre", "Nietzsche"].map((name) => (
                  <div key={name} className="flex items-center gap-2">
                    <div 
                      className="w-2 h-2 rounded-full bg-[#22C55E]"
                    />
                    <span className="text-sm text-[#D4AF37]">{name}</span>
                  </div>
                ))}
                
                <div className="text-xs text-[#64748B] uppercase font-medium mt-4">Élèves ({participants.length})</div>
                {participants.slice(0, 5).map((p, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#22C55E]" />
                    <span className="text-sm text-[#1A365D]">{p.first_name}</span>
                  </div>
                ))}
                {participants.length > 5 && (
                  <span className="text-xs text-[#64748B]">
                    +{participants.length - 5} autres
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GroupChatPage;
